/* eslint-disable react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */
import { captureWebsiteDocument, savePdfResponse } from "utils/documentPdf";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import validator from "validator";
import {
  parseAdditionalQuotationEmails,
  validateAdditionalQuotationEmails,
} from "utils/quotationCustomerValidation";
import {
  LuArrowLeft,
  LuCircleCheck,
  LuCircleX,
  LuClock3,
  LuDownload,
  LuEye,
  LuLock,
  LuMail,
  LuPenLine,
  LuPlus,
  LuReceiptText,
  LuRotateCw,
  LuSend,
  LuX,
} from "react-icons/lu";

import { useMaterialUIController } from "context";
import { loadingAction } from "ReduxReducers/loadingReducer";
import { appConstants } from "utils/appConstants";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { showResMsg } from "utils/generic";
import { genericMessage } from "utils/genericMessages";
import {
  downloadQuotation,
  prepareQuotationEmail,
  prepareQuotationEmailDocument,
  getQuotationDetailsByID,
  viewQuotationDetails,
} from "utils/quotationAPI";
import { fetchActivityLog, saveNotes } from "utils/api";
import { formatActivityTimestamp } from "utils/activityTimestamp";

import AppHeader from "components/AppHeader";
import { PageSkeleton, Skeleton } from "components/Skeleton";
import { SocketContext } from "socket/SocketContext";
import BillingModal from "components/displayModel/billingModel";
import QuotationSendDialog from "./QuotationSendDialog";
import DocumentSentSuccessDialog from "./DocumentSentSuccessDialog";
import { InlineFormMessage } from "components/InlineFeedback/InlineFeedback";
import ShowSwalWithButtons from "components/SwalAlert/swalPopupWithButtons";
import { DashboardNavigation } from "layouts/businessUserDashboard";

import "./quotation.css";

const readLoginDetails = () => {
  try {
    return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS")) || {};
  } catch {
    return {};
  }
};

function ViewQuotation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: QuotationID } = useParams();
  const socket = useContext(SocketContext);
  const [approvalNotice, setApprovalNotice] = useState(false);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Authentication & session details
  const [loginDetails] = useState(readLoginDetails);

  const organisation =
    loginDetails?.businessName ||
    loginDetails?.companyName ||
    loginDetails?.organisationName ||
    loginDetails?.organizationName ||
    loginDetails?.fullName ||
    loginDetails?.name ||
    `${loginDetails?.firstName || ""} ${loginDetails?.lastName || ""}`.trim() ||
    "Invoice & Recover";
  const role =
    sessionStorage.getItem("ROLE_NAME") ||
    loginDetails?.roleName ||
    loginDetails?.role ||
    (loginDetails?.userType === "Business"
      ? "Owner"
      : loginDetails?.userType) ||
    "Owner";
  const userName =
    loginDetails?.fullName ||
    `${loginDetails?.firstName || ""} ${loginDetails?.lastName || ""}`.trim() ||
    loginDetails?.name ||
    "User";

  // Sidebar navigation drawer state
  const [navOpen, setNavOpen] = useState(false);

  // Quotation data state
  const [values, setValues] = useState({
    quotationID: undefined,
    quotationNumber: "",
    contactID: "",
    contactName: "",
    customerEmail: "",
    recipientEmail: "",
    invoiceID: "",
    purchaseOrderNumber: "",
    quotationDate: new Date(),
    validUpto: "14 Days",
    validUptoDate: new Date(),
    quotationStatus: "Sent",
    totalAmount: 0,
    totalDiscountAmount: 0,
    finalAmount: 0,
    paymentInstruction: "",
    terms: "",
    currencyValue: "AUD",
    createdByName: "",
    createdAt: "",
    approvedByName: "",
    approvedDate: "",
    declinedByName: "",
    declinedDate: "",
    declineReason: "",
    expiredDate: "",
    sentByName: "",
    sentDate: "",
    ...(String(location.state?.quotationSummary?._id || "") ===
    String(QuotationID)
      ? location.state.quotationSummary
      : {}),
  });

  const [itemsList, setItemsList] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [documentsList, setDocumentsList] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(true);
  const savedAttachmentsRequestRef = useRef(null);
  const [currencyCode, setCurrencyCode] = useState("AUD");

  // History & Internal Notes
  const [historyRows, setHistoryRows] = useState([]);
  const [internalNote, setInternalNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  // Modals
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [sendRecipient, setSendRecipient] = useState("");
  const [additionalRecipients, setAdditionalRecipients] = useState("");
  const [additionalRecipientsError, setAdditionalRecipientsError] =
    useState("");
  const [sendSubject, setSendSubject] = useState("");
  const [emailAction, setEmailAction] = useState("resend");
  const [sendMessage, setSendMessage] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [sendCopy, setSendCopy] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendError, setSendError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");
  const [showSentSuccess, setShowSentSuccess] = useState(false);
  const emailSuccessRef = useRef(null);
  const sendInFlightRef = useRef(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [activeDocumentUrl, setActiveDocumentUrl] = useState("");
  const [activeDocumentTitle, setActiveDocumentTitle] = useState("");
  const [activeDocumentType, setActiveDocumentType] = useState("");
  const [activeDocumentText, setActiveDocumentText] = useState("");
  const [activeDocumentName, setActiveDocumentName] = useState("");
  const [openingDocument, setOpeningDocument] = useState("");
  const [attachmentError, setAttachmentError] = useState("");
  const documentRequestRef = useRef(null);

  useEffect(() => () => documentRequestRef.current?.abort(), []);
  useEffect(() => () => savedAttachmentsRequestRef.current?.abort(), []);

  useEffect(
    () => () => {
      if (activeDocumentUrl) URL.revokeObjectURL(activeDocumentUrl);
    },
    [activeDocumentUrl]
  );

  useEffect(() => {
    if (emailSuccess) {
      emailSuccessRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [emailSuccess]);

  useEffect(() => {
    if (!location.state?.quotationEmailSuccess) return;
    const { quotationEmailSuccess, ...remainingState } = location.state;
    setEmailSuccess(quotationEmailSuccess);
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: remainingState,
    });
  }, [location, navigate]);

  // Fetch quotation details
  const loadQuotation = useCallback(() => {
    if (!QuotationID) return;

    const finishWithError = (message) => {
      setDetailsLoading(false);
      showResMsg(
        message || "The quotation could not be loaded. Please try again.",
        2000,
        false
      );
      navigate("/quotations");
    };
    const loadFallback = () => {
      getQuotationDetailsByID(
        {
          quotationID: QuotationID,
          businessUserID: loginDetails.businessUserID,
        },
        (fallbackRes) => {
          const fbData =
            fallbackRes?.Result_Output?.[0] || fallbackRes?.Result_Output;
          if (fallbackRes?.Result_Status && fbData) {
            populateQuotation(fbData);
            return;
          }
          finishWithError(fallbackRes?.Result_Message);
        },
        dispatch,
        (error) =>
          finishWithError(
            error?.response?.data?.Result_Message || error?.message
          )
      );
    };

    // Read attachments from the same saved-draft source used by AddQuotation.
    savedAttachmentsRequestRef.current?.abort();
    const attachmentRequest = new AbortController();
    savedAttachmentsRequestRef.current = attachmentRequest;
    setAttachmentsLoading(true);
    setAttachmentError("");
    axios
      .post(
        `${appConstants.baseURL}quotation/fetchQuotationDetailsByID`,
        {
          quotationID: QuotationID,
          businessUserID: loginDetails.businessUserID,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          signal: attachmentRequest.signal,
          timeout: 30000,
        }
      )
      .then(({ data }) => {
        if (attachmentRequest.signal.aborted) return;
        const saved = data?.Result_Output?.[0];
        if (!data?.Result_Status || !saved)
          throw new Error(
            data?.Result_Message ||
              "Attachments could not be loaded. Please refresh the quotation."
          );
        const docs = Array.isArray(saved.quotationDocuments)
          ? saved.quotationDocuments
          : Array.isArray(saved.documentsList)
          ? saved.documentsList
          : [];
        setDocumentsList(
          docs.filter((doc) => {
            if (
              !doc ||
              doc.recordType === "D" ||
              typeof doc.documentFileName !== "string"
            )
              return false;
            const ownerId = doc.quotationID?._id || doc.quotationID;
            if (ownerId && String(ownerId) !== String(QuotationID))
              return false;
            const fileName = doc.documentFileName
              .trim()
              .replace(/\\/g, "/")
              .split("/")
              .pop();
            return (
              Boolean(fileName) &&
              fileName.toLowerCase() !== "customer-review.pdf" &&
              !/^signature_\d+\.jpg$/i.test(fileName)
            );
          })
        );
      })
      .catch((error) => {
        if (!attachmentRequest.signal.aborted) {
          setAttachmentError(
            error.response?.data?.Result_Message ||
              error.message ||
              "Attachments could not be loaded. Please refresh the quotation."
          );
        }
      })
      .finally(() => {
        if (!attachmentRequest.signal.aborted) setAttachmentsLoading(false);
      });

    // Use viewQuotationDetails with fallback to getQuotationDetailsByID
    viewQuotationDetails(
      { quotationID: QuotationID },
      (res) => {
        const raw = res?.Result_Output?.[0] || res?.Result_Output;
        if (res?.Result_Status && raw) {
          populateQuotation(raw);
        } else {
          loadFallback();
        }
      },
      dispatch,
      loadFallback
    );
  }, [QuotationID, dispatch, loginDetails.businessUserID, navigate]);

  const populateQuotation = (data) => {
    const curr = data.currencyValue || data.currency || "AUD";
    setCurrencyCode(curr);

    const items = data.itemsList || data.items || [];
    setItemsList(items);

    setValues({
      ...data,
      quotationNumber:
        data.quotationNumber || `QUO-${QuotationID?.slice(-4) || "1001"}`,
      contactName:
        data.contactName || data.name || data.customerName || "Customer",
      recipientEmail:
        data.recipientEmail ||
        data.customerEmail ||
        data.customerEmailAddress ||
        "",
      quotationStatus: data.quotationStatus || "Sent",
      finalAmount:
        data.finalAmount !== undefined
          ? data.finalAmount
          : data.totalAmount || 0,
      totalAmount: data.totalAmount || 0,
      totalDiscountAmount: data.totalDiscountAmount || 0,
      paymentInstruction: data.paymentInstruction || data.scopeNotes || "",
      terms:
        data.terms ||
        "Listed supply and installation only. Additional work requires approval. This quote is valid for 14 days from sending.",
      createdByName: data.createdByName || userName,
      createdAt: data.createdDate || data.createdAt || new Date(),
      sentByName: data.sentByName || data.createdByName || userName,
      sentDate: data.sentDate || data.updatedAt || data.createdAt || new Date(),
      approvedByName: data.approvedByName || userName,
      approvedDate: data.approvedDate || data.updatedAt || new Date(),
      declinedByName: data.declinedByName || data.rejectedByName || "",
      declinedDate: data.declinedDate || data.rejectedDate || "",
      declineReason: data.declineReason || data.rejectionReason || "",
      expiredDate: data.expiredDate || "",
    });
    setDetailsLoading(false);
  };

  // Fetch activity history
  const fetchQuotationHistory = useCallback(() => {
    if (!QuotationID) return;
    fetchActivityLog(
      {
        _id: QuotationID,
        businessUserID: loginDetails?.businessUserID,
        userID: loginDetails?._id,
        token: loginDetails?.token,
        screenType: "Quotation",
        page: 1,
        perPage: 100,
        sortingType: -1,
        sortingField: "dateAndTime",
        invoiceStatus: "All",
        filterValues: "",
        searchParameterList: ["dateAndTime", "name", "action", "details"],
      },
      (data) => {
        setHistoryRows(data?.Result_Output?.docs || []);
      },
      dispatch
    );
  }, [QuotationID, dispatch, loginDetails]);

  const goToQuotationHistory = () =>
    navigate(`/quotations/activityLog/${QuotationID}`);

  useEffect(() => {
    setDetailsLoading(true);
    loadQuotation();
    fetchQuotationHistory();
  }, [QuotationID, loadQuotation, fetchQuotationHistory]);

  useEffect(() => {
    if (isEmailModalOpen) prepareQuotationEmail();
  }, [isEmailModalOpen]);

  const sendDocumentRef = useRef(null);
  useEffect(() => {
    if (!isEmailModalOpen || !QuotationID) return undefined;
    const prepared = {
      id: String(QuotationID),
      snapshot: captureWebsiteDocument("quotation", QuotationID).catch(
        () => null
      ),
    };
    sendDocumentRef.current = prepared;
    return () => {
      if (sendDocumentRef.current === prepared) sendDocumentRef.current = null;
    };
  }, [isEmailModalOpen, QuotationID]);

  useEffect(() => {
    if (!socket || !loginDetails.businessUserID) return undefined;
    const subscribe = () =>
      socket.emit("message", { businessUserID: loginDetails.businessUserID });
    const receiveApproval = (event) => {
      if (
        String(event.businessUserID) !== String(loginDetails.businessUserID) ||
        String(event.quotationID) !== String(QuotationID)
      )
        return;
      loadQuotation();
      fetchQuotationHistory();
      setApprovalNotice(true);
      showResMsg(
        event.message || "Quotation approved by customer.",
        3000,
        true
      );
    };
    const reconnect = () => {
      subscribe();
      loadQuotation();
      fetchQuotationHistory();
    };
    socket.on("quotationApproved", receiveApproval);
    socket.on("connect", reconnect);
    subscribe();
    return () => {
      socket.off("quotationApproved", receiveApproval);
      socket.off("connect", reconnect);
    };
  }, [
    socket,
    loginDetails.businessUserID,
    QuotationID,
    loadQuotation,
    fetchQuotationHistory,
  ]);

  // Add internal note
  const handleAddInternalNote = () => {
    const note = internalNote.trim();
    if (!note || addingNote) return;

    setAddingNote(true);
    saveNotes(
      {
        _id: QuotationID,
        businessUserID: loginDetails?.businessUserID,
        userID: loginDetails?._id,
        token: loginDetails?.token,
        details: note,
        screenType: "Quotation",
      },
      (data) => {
        setAddingNote(false);
        if (data?.Result_Status) {
          setInternalNote("");
          fetchQuotationHistory();
          showResMsg("Internal note added", 1200, true);
        } else {
          showResMsg(data?.Result_Message || "Unable to add note", 2000, false);
        }
      },
      dispatch
    );
  };

  // Download PDF
  const handleDownload = () =>
    downloadQuotation(
      { _id: QuotationID },
      (response) =>
        savePdfResponse(
          response,
          `quotation_${values.quotationNumber || "details"}.pdf`
        ),
      dispatch
    );

  // Create Invoice (Approved state action)
  const handleCreateInvoice = () => {
    if (values.invoiceID) {
      navigate(`/invoices/view/${values.invoiceID}`);
      return;
    }
    navigate(`/invoices/add?quotationID=${QuotationID}`, {
      state: {
        quotationID: QuotationID,
        quotationNumber: values.quotationNumber,
        quotation: values,
      },
    });
  };

  const handleConfirmCreateInvoice = async () => {
    if (creatingInvoice) return;
    setCreatingInvoice(true);
    dispatch(loadingAction(true));
    const payload = {
      businessUserID: loginDetails?.businessUserID,
      quotationID: QuotationID,
      userID: loginDetails?._id,
    };

    axios
      .post(`${appConstants.baseURL}quotation/createInvoice`, payload, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` },
        timeout: 60000,
      })
      .then((res) => {
        dispatch(loadingAction(false));
        setCreatingInvoice(false);
        const status = res.data.Result_Status;
        if (res.data.Result_Output === "NO AVAILABLE CREDITS") {
          setInvoiceDialogOpen(false);
          setShowBillingModal(true);
        } else {
          const invoiceId = res.data?.Result_Output?._id;
          if (!status || !invoiceId) {
            showResMsg(
              res.data?.Result_Message ||
                "Unable to create the invoice. Please check the invoice list before trying again.",
              2000,
              false
            );
            return;
          }
          setInvoiceDialogOpen(false);
          showResMsg(
            res.data.Result_Message || "Invoice created successfully.",
            2000,
            true
          );
          navigate(`/invoices/edit/${invoiceId}`);
        }
      })
      .catch((error) => {
        dispatch(loadingAction(false));
        setCreatingInvoice(false);
        console.error("Error creating Invoice:", error);
        showResMsg(
          error.response?.data?.Result_Message ||
            error.response?.data?.message ||
            "Unable to create the invoice. Please check the invoice list before trying again.",
          2000,
          false
        );
      });
  };

  // Follow up opens email review; resend uses the saved recipient directly.
  const recipientError = !sendRecipient.trim()
    ? "Please enter a recipient email."
    : validator.isEmail(sendRecipient.trim())
    ? ""
    : "Please enter a valid email address, e.g. accounts@example.com.";

  const handleOpenEmailModal = (action = "resend") => {
    if (sendInFlightRef.current) return;
    setEmailAction(action);
    setSendMessage(
      action === "followUp"
        ? `Just following up on quote ${
            values.quotationNumber || "QUO"
          } for $${formatCurrencyAUD(parseFloat(values.finalAmount) || 0)} ${
            currencyCode || "AUD"
          }. You can review the details and approve your quote online.`
        : ""
    );
    setSendRecipient(String(values.recipientEmail || "").trim());
    setAdditionalRecipients("");
    setAdditionalRecipientsError("");
    setSendSubject(
      `Quote ${
        values.quotationNumber
      } from ${organisation} · $${formatCurrencyAUD(
        parseFloat(values.finalAmount) || 0
      )} ${currencyCode || "AUD"}`
    );
    setAttachPdf(true);
    setSendCopy(false);
    setSendError("");
    setEmailSuccess("");
    setIsEmailModalOpen(true);
  };

  const handleSendEmail = async ({ directResend = false } = {}) => {
    if (sendInFlightRef.current) return;
    const recipient = directResend
      ? String(values.recipientEmail || "").trim()
      : sendRecipient.trim();
    const action = directResend ? "resend" : emailAction;
    if (directResend) {
      setEmailAction("resend");
      setEmailSuccess("");
    }
    const primaryError = directResend
      ? !validator.isEmail(recipient)
        ? "A valid customer email is required to resend this quotation. Update the customer email and try again."
        : ""
      : recipientError;
    const additionalError = directResend
      ? ""
      : validateAdditionalQuotationEmails(additionalRecipients);
    setAdditionalRecipientsError(additionalError);
    if (primaryError || additionalError) {
      setSendError(primaryError);
      if (directResend) showResMsg(primaryError, 4500, false);
      if (additionalError)
        document.getElementById("followup-send-recipient")?.focus();
      return;
    }
    if (action === "followUp" && !sendMessage.trim()) {
      setSendError("Please enter an email message.");
      document.getElementById("followup-send-recipient-message")?.focus();
      return;
    }
    sendInFlightRef.current = true;
    setSendingEmail(true);
    setSendError("");
    try {
      const prepared = sendDocumentRef.current;
      const documentPayload = await prepareQuotationEmailDocument(
        QuotationID,
        prepared?.id === String(QuotationID) ? prepared.snapshot : undefined
      );
      const { data } = await axios.post(
        `${appConstants.baseURL}quotation/emailReminderQuotation`,
        {
          quotationID: QuotationID,
          businessUserID: loginDetails.businessUserID,
          userID: loginDetails._id,
          recipientEmail: recipient,
          additionalRecipientEmails: directResend
            ? []
            : parseAdditionalQuotationEmails(additionalRecipients),
          emailSubject: directResend
            ? `Quote ${
                values.quotationNumber
              } from ${organisation} · $${formatCurrencyAUD(
                parseFloat(values.finalAmount) || 0
              )} ${currencyCode || "AUD"}`
            : sendSubject.trim(),
          ...(action === "followUp"
            ? {
                communicationType: "followUp",
                emailMessage: sendMessage.trim(),
              }
            : {}),
          sendCopy: directResend ? false : sendCopy,
          attachPdf: directResend ? true : attachPdf,
          ...documentPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          timeout: 60000,
        }
      );
      if (!data?.Result_Status) {
        if (data?.Result_Output?.partiallySent) {
          loadQuotation();
          fetchQuotationHistory();
        }
        throw new Error(
          data?.Result_Message || "The quotation could not be sent."
        );
      }
      setIsEmailModalOpen(false);
      const successMessage = directResend
        ? `Quotation ${values.quotationNumber} was resent to ${recipient}.`
        : action === "followUp"
        ? `Follow-up sent for quote ${values.quotationNumber || ""}.`
        : "Email sent successfully.";
      setEmailSuccess(successMessage);
      setShowSentSuccess(true);
      loadQuotation();
      fetchQuotationHistory();
    } catch (error) {
      const errorMessage =
        error.response?.data?.Result_Message ||
        (directResend && error.code === "ECONNABORTED"
          ? "Sending took longer than expected. Check the quotation activity before resending again."
          : error.message || "The quotation could not be sent.");
      setSendError(errorMessage);
      if (directResend) showResMsg(errorMessage, 4500, false);
    } finally {
      sendInFlightRef.current = false;
      setSendingEmail(false);
    }
  };

  // Revise action
  const handleRevise = () => {
    navigate(`/quotations/edit/${QuotationID}`, {
      state: { revisionRequested: true },
    });
  };

  // View uploaded document
  const handleViewDocument = async (doc) => {
    if (documentRequestRef.current) return;
    setAttachmentError("");
    const storedPath =
      typeof doc?.documentFileName === "string"
        ? doc.documentFileName.trim().replace(/\\/g, "/").replace(/^\/+/, "")
        : "";
    if (!storedPath) {
      setAttachmentError(
        "This attachment is unavailable. Please upload it again."
      );
      return;
    }
    const request = new AbortController();
    documentRequestRef.current = request;
    setOpeningDocument(doc.documentFileName);
    try {
      const { data } = await axios.get(
        `${appConstants.baseURL}quotation/viewUploadedQuotationDocument`,
        {
          params: { fileName: storedPath },
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          signal: request.signal,
          timeout: 30000,
        }
      );
      const mimeType = data.type.split(";")[0].toLowerCase();
      const extension = storedPath.split(".").pop().toLowerCase();
      if (mimeType === "application/json") {
        const result = JSON.parse(await data.text());
        if (result?.Result_Status === false)
          throw new Error(
            result.Result_Message || "This attachment is unavailable."
          );
      }
      if (
        !data.size ||
        (mimeType === "text/html" && !["html", "htm"].includes(extension))
      ) {
        throw new Error(
          "The attachment could not be loaded. Please try again."
        );
      }
      const type =
        mimeType === "application/pdf" || extension === "pdf"
          ? "pdf"
          : mimeType.startsWith("image/") ||
            /^(png|jpe?g|gif|webp|bmp|svg)$/i.test(extension) ||
            doc.fileType === "image"
          ? "image"
          : (mimeType.startsWith("text/") && mimeType !== "text/html") ||
            ["txt", "csv", "json", "log"].includes(extension)
          ? "text"
          : "download";
      const text = type === "text" ? await data.text() : "";
      if (request.signal.aborted) return;
      setActiveDocumentText(text);
      setActiveDocumentType(type);
      setActiveDocumentName(
        `${doc.documentName || "Attachment"}${
          doc.documentName?.toLowerCase().endsWith(`.${extension}`)
            ? ""
            : `.${extension}`
        }`
      );
      setActiveDocumentUrl(
        URL.createObjectURL(
          type === "pdf" ? new Blob([data], { type: "application/pdf" }) : data
        )
      );
      setActiveDocumentTitle(doc.documentName || "Document Viewer");
      setDocumentViewerOpen(true);
    } catch (error) {
      if (request.signal.aborted) return;
      let message = "The attachment could not be opened. Please try again.";
      if (error.response?.data?.type?.includes("application/json")) {
        try {
          message =
            JSON.parse(await error.response.data.text()).Result_Message ||
            message;
        } catch {
          /* Keep the readable fallback. */
        }
      } else if (
        !error.response &&
        error.code !== "ECONNABORTED" &&
        !error.isAxiosError
      ) {
        message = error.message || message;
      }
      setAttachmentError(message);
    } finally {
      if (!request.signal.aborted) setOpeningDocument("");
      if (documentRequestRef.current === request)
        documentRequestRef.current = null;
    }
  };

  // Formatted date values
  const formattedCreationDate = useMemo(() => {
    const d = new Date(values.createdAt || values.quotationDate || Date.now());
    return d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [values.createdAt, values.quotationDate]);

  const formattedQuoteDate = useMemo(() => {
    const d = new Date(values.quotationDate || Date.now());
    return d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [values.quotationDate]);

  const formattedSentDate = useMemo(() => {
    const d = new Date(values.sentDate || values.createdAt || Date.now());
    const day = d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${day} · ${time} AEST`;
  }, [values.sentDate, values.createdAt]);

  const formattedApprovalDate = useMemo(() => {
    const d = new Date(values.approvedDate || values.updatedAt || Date.now());
    const day = d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${day} · ${time} AEST`;
  }, [values.approvedDate, values.updatedAt]);

  const formattedDeclinedDate = useMemo(() => {
    const d = new Date(values.declinedDate || values.updatedAt || Date.now());
    const day = d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${day} · ${time} AEST`;
  }, [values.declinedDate, values.updatedAt]);

  const formattedExpiredDate = useMemo(() => {
    const d = new Date(
      values.expiredDate || values.validUptoDate || Date.now()
    );
    return d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [values.expiredDate, values.validUptoDate]);

  const formattedValidUntil = useMemo(() => {
    const d = new Date(values.validUptoDate || Date.now() + 14 * 86400000);
    return d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, [values.validUptoDate]);

  const formattedViewedDate = useMemo(() => {
    const d = new Date(values.viewedDate || values.updatedAt || Date.now());
    const day = d.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${day} · ${time} AEST`;
  }, [values.viewedDate, values.updatedAt]);

  const validDaysRemaining = useMemo(() => {
    try {
      const validDate = new Date(
        values.validUptoDate || Date.now() + 14 * 86400000
      );
      const sentDate = new Date(
        values.sentDate || values.createdAt || Date.now()
      );
      const diffDays = Math.max(
        1,
        Math.round((validDate - sentDate) / (1000 * 60 * 60 * 24))
      );
      return isNaN(diffDays) ? 14 : diffDays;
    } catch {
      return 14;
    }
  }, [values.validUptoDate, values.sentDate, values.createdAt]);

  // Financial calculations
  const totalAmount = parseFloat(values.finalAmount || values.totalAmount || 0);
  const totalDiscounts = parseFloat(values.totalDiscountAmount || 0);
  // GST 10% included (Australian standard 1/11th inclusive)
  const subtotalExGst =
    values.gstType === "No GST"
      ? totalAmount
      : totalAmount > 0
      ? totalAmount / 1.1
      : 0;
  const gstAmount = totalAmount > 0 ? totalAmount - subtotalExGst : 0;

  const quoteStatus = String(values.quotationStatus || "sent")
    .trim()
    .toLowerCase();
  const isApproved = quoteStatus === "approved";
  const isViewed = quoteStatus === "viewed";
  const isDeclined = ["declined", "rejected"].includes(quoteStatus);
  const isExpired = ["expired", "expire"].includes(quoteStatus);
  const isSent = quoteStatus === "sent";
  const isQuotationLoaded =
    Boolean(QuotationID) &&
    String(values._id || values.quotationID || "") === String(QuotationID);

  if (detailsLoading || !isQuotationLoaded) {
    return (
      <div
        className={`dashboard-shell quotation-editor-shell quotation-details-shell${
          darkMode ? " dark" : ""
        }`}
      >
        <DashboardNavigation
          open={navOpen}
          onClose={() => setNavOpen(false)}
          activePath="/quotations"
          organisation={organisation}
          role={role}
        />
        <div className="dashboard-main quotation-editor-main-area">
          <AppHeader onOpenNav={() => setNavOpen(true)} userName={userName} />
          <main
            className={`quote-details-content quotation-editor-page${
              darkMode ? " dark" : ""
            }`}
          >
            <PageSkeleton
              type="detail"
              variant="document"
              label="Loading quotation details"
            />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`dashboard-shell quotation-editor-shell quotation-details-shell quote-details-state-${quoteStatus}${
        darkMode ? " dark" : ""
      }`}
    >
      {/* SIDEBAR NAVIGATION */}
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/quotations"
        organisation={organisation}
        role={role}
      />

      <div className="dashboard-main quotation-editor-main-area">
        {/* FIXED UNTOUCHABLE APP HEADER (Figma Node 429:22151) */}
        <AppHeader
          onOpenNav={() => setNavOpen(true)}
          userName={userName}
          notificationCount={approvalNotice ? 1 : 0}
          onNotificationClick={() => navigate("/quotations?notifications=true")}
        />

        {/* MAIN DETAILS CONTENT */}
        <main
          className={`quote-details-content quotation-editor-page${
            darkMode ? " dark" : ""
          }`}
        >
          {/* HEADER: Back link, Title, Badges, Action buttons */}
          <div className="quote-details-header">
            {/* Back link */}
            <button
              type="button"
              className="quote-details-back-btn"
              onClick={() => navigate("/quotations")}
            >
              <LuArrowLeft />
              <span>Back to Quotations</span>
            </button>

            {/* Title row */}
            <div className="quote-details-title-row">
              <div className="quote-details-title-group">
                <h1 className="quote-details-title">Quote details</h1>
                <div className="quote-details-identity-row">
                  <span className="quote-number-pill">
                    {values.quotationNumber || "QUO-1077"}
                  </span>
                  <span className="quote-identity-dot">·</span>
                  {values.contactID ? (
                    <Link
                      to={`/contacts/${values.contactID}`}
                      className="quote-customer-name quote-customer-link"
                    >
                      {values.contactName || "Beacon Plumbing"}
                    </Link>
                  ) : (
                    <span className="quote-customer-name">
                      {values.contactName || "Beacon Plumbing"}
                    </span>
                  )}
                  <span className="quote-identity-dot">·</span>
                  {isApproved ? (
                    <span className="quote-status-pill approved">Approved</span>
                  ) : isViewed ? (
                    <span className="quote-status-pill viewed">Viewed</span>
                  ) : isDeclined ? (
                    <span className="quote-status-pill declined">Declined</span>
                  ) : isExpired ? (
                    <span className="quote-status-pill expired">Expired</span>
                  ) : isSent ? (
                    <span className="quote-status-pill sent">Sent</span>
                  ) : (
                    <span
                      className={`quote-status-pill ${String(
                        values.quotationStatus || "draft"
                      ).toLowerCase()}`}
                    >
                      {values.quotationStatus || "Sent"}
                    </span>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              {isQuotationLoaded && (
                <div className="quote-details-actions">
                  <button
                    type="button"
                    className="quote-details-action-btn"
                    onClick={() =>
                      navigate(
                        `/quotations/quotation/${
                          values.quotationNumber || "quotation"
                        }/${QuotationID}`,
                        {
                          state: {
                            previewReturn: {
                              path: `/quotations/view/${QuotationID}`,
                              label: "Back to Quote details",
                            },
                          },
                        }
                      )
                    }
                  >
                    <LuEye />
                    <span>Preview</span>
                  </button>
                  <button
                    type="button"
                    className="quote-details-action-btn"
                    onClick={handleDownload}
                  >
                    <LuDownload />
                    <span>Download PDF</span>
                  </button>
                  {!isApproved && (
                    <button
                      type="button"
                      className="quote-details-action-btn"
                      onClick={handleRevise}
                    >
                      <LuPenLine />
                      <span>Revise</span>
                    </button>
                  )}

                  {/* Approved state: Create invoice / View invoice button */}
                  {isApproved && (
                    <button
                      type="button"
                      className="quote-details-primary-btn"
                      onClick={handleCreateInvoice}
                    >
                      <LuReceiptText />
                      <span>
                        {values.invoiceID ? "View invoice" : "Create invoice"}
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Subtitle / Meta row */}
            <p className="quote-details-meta-line">
              {organisation} · All amounts in {currencyCode} · Created by{" "}
              {values.createdByName || userName} · {formattedCreationDate}
            </p>
          </div>

          {!isEmailModalOpen && emailAction === "resend" && sendError && (
            <InlineFormMessage
              message={sendError}
              type="error"
              scope="quotation-resend"
              autoClose={0}
              onClose={() => setSendError("")}
            />
          )}

          {emailSuccess && (
            <div
              ref={emailSuccessRef}
              className="quotation-send-success-banner"
              role="status"
            >
              <LuCircleCheck aria-hidden="true" />
              <span>{emailSuccess}</span>
              <button
                type="button"
                aria-label="Dismiss sent confirmation"
                onClick={() => setEmailSuccess("")}
              >
                <LuX />
              </button>
            </div>
          )}

          {/* TWO-COLUMN GRID */}
          <div className="quote-details-grid">
            {/* LEFT COLUMN: Record, Items & Pricing, Attachments */}
            <div className="quote-details-main-col">
              {/* CARD 1: QUOTE CONTENT */}
              <section className="quote-details-card">
                <div className="quote-card-header">
                  <h3 className="quote-card-title">Quote content</h3>
                  <p className="quote-card-subtitle">
                    {isApproved
                      ? "Read-only approved version. Revise creates a new revision and preserves the customer’s approved copy."
                      : isDeclined
                      ? "This quote was declined. Revise creates a new revision and preserves the customer response."
                      : isExpired
                      ? "This quote has expired. Revise creates a new revision and preserves the original quote."
                      : "Read-only sent version. Revise creates a new revision and preserves the customer’s sent copy."}
                  </p>
                </div>

                <div className="quote-content-meta-grid">
                  <div className="quote-meta-item">
                    <span className="quote-meta-label">Prepared for</span>
                    <strong className="quote-meta-val">
                      {values.contactID ? (
                        <Link
                          to={`/contacts/${values.contactID}`}
                          className="quote-customer-link"
                        >
                          {values.contactName || "Beacon Plumbing"}
                        </Link>
                      ) : (
                        values.contactName || "Beacon Plumbing"
                      )}
                    </strong>
                  </div>
                  <div className="quote-meta-item">
                    <span className="quote-meta-label">Quote date</span>
                    <strong className="quote-meta-val">
                      {formattedQuoteDate}
                    </strong>
                  </div>
                </div>

                <hr className="quote-card-divider" />

                {/* ITEMS & PRICING TABLE */}
                <div className="quote-card-header">
                  <h3 className="quote-card-title">Items &amp; pricing</h3>
                  <p className="quote-card-subtitle">
                    {values.gstType === "No GST"
                      ? "No GST applies"
                      : "Unit prices and line totals include GST"}{" "}
                    · {currencyCode}
                  </p>
                </div>

                <div className="quote-items-table-wrap">
                  <div className="quote-items-table-header">
                    <span className="quote-th">Description</span>
                    <span className="quote-th text-right">Qty</span>
                    <span className="quote-th text-right">Unit price</span>
                    <span className="quote-th text-right">GST</span>
                    <span className="quote-th text-right">Discount</span>
                    <span className="quote-th text-right">Line total</span>
                  </div>

                  <div className="quote-items-table-body">
                    {itemsList.length > 0 ? (
                      itemsList.map((item, index) => {
                        const price = parseFloat(item.itemPrice || 0);
                        const qty = parseFloat(item.itemQuantity || 1);
                        const discVal = item.discountValue;
                        const discType = item.discountType || "None";
                        const discAmt = parseFloat(item.discountAmount || 0);
                        const lineTotal =
                          item.totalItemPrice !== undefined
                            ? parseFloat(item.totalItemPrice || 0)
                            : price * qty - discAmt;

                        let discDisplay = "None";
                        if (discVal && discType !== "None") {
                          discDisplay =
                            discType === "Percentage" || discType === "%"
                              ? `${discVal}%`
                              : `$${formatCurrencyAUD(
                                  parseFloat(discVal) || 0
                                )}`;
                        }

                        return (
                          <div
                            className="quote-item-row"
                            key={item._id || item.itemID || index}
                          >
                            <div className="quote-item-desc">
                              <span className="quote-item-name">
                                {item.itemName || "Item"}
                              </span>
                              {item.itemDescription && (
                                <span className="quote-item-sub">
                                  {item.itemDescription}
                                </span>
                              )}
                            </div>
                            <span className="quote-cell-qty">{qty}</span>
                            <span className="quote-cell-money">
                              ${formatCurrencyAUD(price)}
                            </span>
                            <span className="quote-cell-gst">
                              {values.gstType === "No GST"
                                ? "0%"
                                : item.gst !== undefined
                                ? `${item.gst}%`
                                : "10%"}
                            </span>
                            <span className="quote-cell-discount">
                              {discDisplay}
                            </span>
                            <span className="quote-cell-money">
                              ${formatCurrencyAUD(lineTotal)}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="quote-item-row">
                        <div className="quote-item-desc">
                          <span className="quote-item-name">
                            Switchboard safety upgrade
                          </span>
                        </div>
                        <span className="quote-cell-qty">1</span>
                        <span className="quote-cell-money">$2,500.00</span>
                        <span className="quote-cell-gst">10%</span>
                        <span className="quote-cell-discount">None</span>
                        <span className="quote-cell-money">$2,500.00</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* SCOPE, TERMS & TOTALS BREAKDOWN */}
                <div className="quote-scope-totals-row">
                  {/* Left sub-col: Scope / customer notes, Terms */}
                  <div className="quote-scope-col">
                    {values.paymentInstruction?.trim() && (
                      <div className="quote-scope-block">
                        <h4 className="quote-scope-title">
                          Scope / customer notes
                        </h4>
                        <p className="quote-scope-desc">
                          {values.paymentInstruction}
                        </p>
                      </div>
                    )}

                    <div className="quote-scope-block">
                      <h4 className="quote-scope-title">Terms</h4>
                      <p className="quote-scope-desc">
                        {values.terms ||
                          "Listed supply and installation only. Additional work requires approval. This quote is valid for 14 days from sending."}
                      </p>
                    </div>
                  </div>

                  {/* Right sub-col: Totals summary breakdown */}
                  <div className="quote-totals-col">
                    <div className="quote-total-row">
                      <span>
                        {values.gstType === "No GST"
                          ? "Total item discounts"
                          : "Total item discounts (GST inclusive)"}
                      </span>
                      <strong className="quote-total-val">
                        ${formatCurrencyAUD(totalDiscounts)}
                      </strong>
                    </div>

                    <div className="quote-total-row">
                      <span>Subtotal excluding GST (after item discounts)</span>
                      <strong className="quote-total-val">
                        ${formatCurrencyAUD(subtotalExGst)}
                      </strong>
                    </div>

                    <div className="quote-total-row">
                      <span>
                        {values.gstType === "No GST"
                          ? "No GST"
                          : "GST 10% (included)"}
                      </span>
                      <strong className="quote-total-val">
                        ${formatCurrencyAUD(gstAmount)}
                      </strong>
                    </div>

                    <div className="quote-grand-total-row">
                      <span>
                        {values.gstType === "No GST"
                          ? "Total"
                          : "Total including GST"}{" "}
                        · {currencyCode}
                      </span>
                      <strong className="quote-grand-total-val">
                        ${formatCurrencyAUD(totalAmount)}
                      </strong>
                    </div>

                    <p className="quote-totals-footnote">
                      {values.gstType === "No GST"
                        ? "No GST applies. Discounts are already applied."
                        : "GST is included in the total, not added again. Discounts are already applied."}
                    </p>
                  </div>
                </div>

                <hr className="quote-card-divider" />

                {/* ATTACHMENTS */}
                <div className="quote-card-header">
                  <h3 className="quote-card-title">Attachments</h3>
                </div>

                <div className="quote-attachments-body">
                  {attachmentError && (
                    <InlineFormMessage
                      message={attachmentError}
                      type="error"
                      scope="quotation-attachment"
                      autoClose={0}
                      onClose={() => setAttachmentError("")}
                    />
                  )}
                  {attachmentsLoading ? (
                    <div
                      className="quote-attachments-list"
                      role="status"
                      aria-label="Loading attachments"
                    >
                      {[0, 1].map((item) => (
                        <div
                          className="quote-attachment-item"
                          aria-hidden="true"
                          key={item}
                        >
                          <div className="quote-attachment-info">
                            <Skeleton width="min(240px, 72%)" height={14} />
                          </div>
                          <Skeleton variant="circular" width={32} height={32} />
                        </div>
                      ))}
                    </div>
                  ) : documentsList.length > 0 ? (
                    <div className="quote-attachments-list">
                      {documentsList.map((doc, idx) => (
                        <div
                          className="quote-attachment-item"
                          key={doc._id || idx}
                        >
                          <div className="quote-attachment-info">
                            <span>
                              {doc.documentName ||
                                doc.documentFileName.split(/[\\/]/).pop()}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="quote-attachment-btn"
                            disabled={Boolean(openingDocument)}
                            aria-busy={
                              openingDocument === doc.documentFileName ||
                              undefined
                            }
                            aria-label={`View ${
                              doc.documentName || "attachment"
                            }`}
                            title="View attachment"
                            onClick={() => handleViewDocument(doc)}
                          >
                            <LuEye aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    !attachmentError && (
                      <p className="quote-attachments-empty">
                        No attachments added
                      </p>
                    )
                  )}
                </div>
              </section>
            </div>

            {/* RIGHT COLUMN: Sidebar Cards */}
            <aside className="quote-details-sidebar">
              {/* CARD 1: Total including GST */}
              <div className="quote-summary-total-card">
                <div className="quote-summary-total-header">
                  <h3 className="quote-card-title">
                    {values.gstType === "No GST"
                      ? "Total"
                      : "Total including GST"}
                  </h3>
                  <LuReceiptText aria-hidden="true" />
                </div>
                <div>
                  <p className="quote-summary-amount">
                    ${formatCurrencyAUD(totalAmount)}
                  </p>
                  <p className="quote-summary-caption">
                    {isApproved
                      ? `${currencyCode} · Approved · Not invoiced`
                      : isDeclined
                      ? `${currencyCode} · Declined`
                      : isExpired
                      ? `${currencyCode} · Expired`
                      : `${currencyCode} · ${
                          itemsList.length || 3
                        } items · Potential business`}
                  </p>
                </div>
              </div>

              {/* CARD 2: STATUS SPECIFIC CARD */}
              {isApproved ? (
                /* APPROVED STATUS CARD (Figma Node 623:51504) */
                <div className="quote-sidebar-status-card approved">
                  <div className="quote-status-card-header approved">
                    <LuCircleCheck />
                    <span>Approval recorded</span>
                  </div>

                  <div className="quote-status-meta-list">
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Recorded by
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {values.approvedByName || userName}
                      </strong>
                    </div>

                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Recorded on
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {formattedApprovalDate}
                      </strong>
                    </div>

                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Method
                      </span>
                      <strong className="quote-status-meta-row-val">
                        Recorded manually
                      </strong>
                      <span className="quote-meta-footnote">
                        Approval received outside the product.
                      </span>
                    </div>
                  </div>

                  {/* Ready to invoice box */}
                  <div className="ready-to-invoice-box">
                    <h5 className="ready-to-invoice-title">Ready to invoice</h5>
                    <p className="ready-to-invoice-desc">
                      {values.invoiceID
                        ? `Invoice created with ID ${values.invoiceID}.`
                        : "No invoice created. Create invoice opens a review before conversion."}
                    </p>
                  </div>

                  {/* Original delivery box */}
                  <div className="quote-original-delivery-box">
                    <h5 className="quote-scope-title">Original delivery</h5>
                    <div className="quote-status-meta-list">
                      <div className="quote-status-meta-row">
                        <span className="quote-status-meta-row-label">
                          Recipient
                        </span>
                        <strong className="quote-status-meta-row-val">
                          {values.recipientEmail ||
                            "accounts@beaconplumbing.example"}
                        </strong>
                      </div>
                      <div className="quote-status-meta-row">
                        <span className="quote-status-meta-row-label">
                          Sent
                        </span>
                        <strong className="quote-status-meta-row-val">
                          {formattedSentDate}
                        </strong>
                      </div>
                      <div className="quote-status-meta-row">
                        <span className="quote-status-meta-row-label">
                          Sent by
                        </span>
                        <strong className="quote-status-meta-row-val">
                          {values.sentByName || userName}
                        </strong>
                      </div>
                      <div className="quote-status-meta-row">
                        <span className="quote-status-meta-row-label">
                          Valid until
                        </span>
                        <strong className="quote-status-meta-row-val">
                          {formattedValidUntil} · 14 days from sending
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isDeclined ? (
                <div className="quote-sidebar-status-card declined">
                  <div className="quote-status-card-header declined">
                    <LuCircleX />
                    <span>Declined by customer</span>
                  </div>

                  <div className="quote-status-meta-list">
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Declined by
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {values.declinedByName ||
                          values.contactName ||
                          "Customer"}
                      </strong>
                    </div>
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Declined on
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {formattedDeclinedDate}
                      </strong>
                    </div>
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Reason
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {values.declineReason || "No reason provided"}
                      </strong>
                    </div>
                  </div>

                  <div className="quote-original-delivery-box">
                    <h5 className="quote-scope-title">Original delivery</h5>
                    <div className="quote-status-meta-list">
                      <div className="quote-status-meta-row">
                        <span className="quote-status-meta-row-label">
                          Recipient
                        </span>
                        <strong className="quote-status-meta-row-val">
                          {values.recipientEmail ||
                            "accounts@beaconplumbing.example"}
                        </strong>
                      </div>
                      <div className="quote-status-meta-row">
                        <span className="quote-status-meta-row-label">
                          Sent
                        </span>
                        <strong className="quote-status-meta-row-val">
                          {formattedSentDate}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="quote-status-sub-btn quote-status-revise-btn"
                    onClick={handleRevise}
                  >
                    <LuPenLine />
                    <span>Revise quote</span>
                  </button>
                </div>
              ) : isExpired ? (
                <div className="quote-sidebar-status-card expired">
                  <div className="quote-status-card-header expired">
                    <LuClock3 />
                    <span>Quote expired</span>
                  </div>

                  <div className="quote-status-meta-list">
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Expired on
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {formattedExpiredDate}
                      </strong>
                    </div>
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Valid until
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {formattedValidUntil}
                      </strong>
                    </div>
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Recipient
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {values.recipientEmail ||
                          "accounts@beaconplumbing.example"}
                      </strong>
                    </div>
                  </div>

                  <div className="quote-status-card-actions">
                    <button
                      type="button"
                      className="quote-status-sub-btn"
                      onClick={() => handleOpenEmailModal("followUp")}
                    >
                      <LuMail />
                      <span>Follow up</span>
                    </button>
                    <button
                      type="button"
                      className="quote-status-sub-btn"
                      onClick={handleRevise}
                    >
                      <LuPenLine />
                      <span>Revise</span>
                    </button>
                  </div>
                  <p className="quote-meta-footnote">
                    Revise creates a new quote version before it is sent to the
                    customer.
                  </p>
                </div>
              ) : isViewed ? (
                /* VIEWED STATUS CARD (Figma Node 663:64332) */
                <div className="quote-sidebar-status-card viewed">
                  <div className="quote-status-card-header">
                    <span>Sent to customer</span>
                  </div>

                  <div className="quote-status-meta-list">
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Recipient
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {values.recipientEmail ||
                          "accounts@beaconplumbing.example"}
                      </strong>
                    </div>

                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">Sent</span>
                      <strong className="quote-status-meta-row-val">
                        {formattedSentDate}
                      </strong>
                      <span className="quote-meta-subtext">
                        Sent by {values.sentByName || userName}
                      </span>
                    </div>

                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Quote link viewed
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {formattedViewedDate}
                      </strong>
                      <span className="quote-meta-subtext">
                        Recorded by System · Viewer identity unavailable. A link
                        view is not approval.
                      </span>
                    </div>

                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Valid until
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {formattedValidUntil}
                      </strong>
                      <span className="quote-meta-subtext">
                        Active · {validDaysRemaining} days from sending
                      </span>
                    </div>
                  </div>

                  {/* Actions: Stacked full-width buttons in Viewed state */}
                  <div className="quote-status-card-actions viewed-actions">
                    <button
                      type="button"
                      className="quote-status-primary-btn"
                      disabled={sendingEmail}
                      onClick={() => handleOpenEmailModal("followUp")}
                    >
                      <LuMail />
                      <span>Follow up</span>
                    </button>
                    <button
                      type="button"
                      className="quote-status-secondary-btn"
                      disabled={sendingEmail}
                      aria-busy={sendingEmail || undefined}
                      onClick={() => handleSendEmail({ directResend: true })}
                    >
                      <LuRotateCw />
                      <span>{sendingEmail ? "Resending..." : "Resend"}</span>
                    </button>
                  </div>

                  <p className="quote-meta-footnote">
                    Resend sends the same quote again. Follow up starts a new
                    communication.
                  </p>
                </div>
              ) : (
                /* SENT STATUS CARD (Figma Node 623:40457) */
                <div className="quote-sidebar-status-card sent">
                  <div className="quote-status-card-header">
                    <span>Sent to customer</span>
                  </div>

                  <div className="quote-status-meta-list">
                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Recipient
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {values.recipientEmail ||
                          "accounts@beaconplumbing.example"}
                      </strong>
                    </div>

                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">Sent</span>
                      <strong className="quote-status-meta-row-val">
                        {formattedSentDate}
                      </strong>
                    </div>

                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Sent by
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {values.sentByName || userName}
                      </strong>
                    </div>

                    <div className="quote-status-meta-row">
                      <span className="quote-status-meta-row-label">
                        Valid until
                      </span>
                      <strong className="quote-status-meta-row-val">
                        {formattedValidUntil} · 14 days from sending
                      </strong>
                    </div>
                  </div>

                  {/* Actions: Follow up & Resend */}
                  <div className="quote-status-card-actions">
                    <button
                      type="button"
                      className="quote-status-sub-btn"
                      disabled={sendingEmail}
                      onClick={() => handleOpenEmailModal("followUp")}
                    >
                      <LuMail />
                      <span>Follow up</span>
                    </button>
                    <button
                      type="button"
                      className="quote-status-sub-btn"
                      disabled={sendingEmail}
                      aria-busy={sendingEmail || undefined}
                      onClick={() => handleSendEmail({ directResend: true })}
                    >
                      <LuRotateCw />
                      <span>{sendingEmail ? "Resending..." : "Resend"}</span>
                    </button>
                  </div>

                  <p className="quote-meta-footnote">
                    Resend sends the same quote again. Follow up starts a new
                    communication.
                  </p>
                </div>
              )}

              {/* CARD 3: Callout guidance footnote */}
              <p className="quote-sidebar-disclaimer">
                {isApproved
                  ? "Approved does not mean invoiced or paid. Quote value is not money owed or collected."
                  : isDeclined
                  ? "A declined quote remains available for your records. Revise it to send a new version."
                  : isExpired
                  ? "An expired quote cannot be accepted. Revise it to send a new version."
                  : "Quote value is potential business, not money owed or collected."}
              </p>
            </aside>
          </div>

          {/* BOTTOM SECTION: HISTORY & NOTES (Figma Nodes 623:40457 & 623:51504) */}
          <section className="quote-details-history-section">
            <div className="quote-history-header">
              <div>
                <h3 className="quote-history-title">History &amp; notes</h3>
                <p className="quote-history-subtitle">
                  {isViewed
                    ? "All activity · Newest first · Audit events cannot be edited."
                    : "All activity · Audit events cannot be edited."}
                </p>
              </div>
              <button
                type="button"
                className="quotation-history-link-btn"
                onClick={goToQuotationHistory}
              >
                View full history
              </button>
            </div>

            <div className="quote-history-privacy-banner">
              <LuLock />
              <div>
                <strong>Only visible to your team</strong>
                <p>
                  History and internal notes never appear on the customer
                  document or PDF.
                </p>
              </div>
            </div>

            <div className="quote-history-two-col">
              {/* Left col: Full activity history feed */}
              <div className="quote-history-activity-col">
                {historyRows.length > 0 ? (
                  historyRows.slice(0, 3).map((row, idx) => {
                    const isNote = /note/i.test(
                      `${row.action || ""} ${row.details || ""}`
                    );
                    return (
                      <article
                        className={`quotation-history-event${
                          isNote ? " note" : ""
                        }`}
                        key={row._id || `${row.dateAndTime}-${idx}`}
                      >
                        <strong>
                          {isNote
                            ? "Internal note"
                            : row.action || "Updated quote"}
                        </strong>
                        <time>
                          {formatActivityTimestamp(
                            row.dateAndTime,
                            formattedCreationDate
                          )}
                        </time>
                        <span>
                          {row.name || values.createdByName || userName}
                        </span>
                        <p>{row.details || "Quotation record updated."}</p>
                      </article>
                    );
                  })
                ) : (
                  /* Fallback items if no history loaded yet */
                  <>
                    {isApproved && (
                      <article className="quotation-history-event">
                        <strong>Approval recorded</strong>
                        <time>{formattedApprovalDate}</time>
                        <span>{values.approvedByName || userName}</span>
                        <p>
                          Manual approval recorded for{" "}
                          {values.quotationNumber || "QUO-1077"}. Total $
                          {formatCurrencyAUD(totalAmount)} {currencyCode}{" "}
                          unchanged.
                        </p>
                      </article>
                    )}
                    {isViewed && (
                      <article className="quotation-history-event">
                        <strong>Link viewed</strong>
                        <time>{formattedViewedDate}</time>
                        <span>Customer link / System</span>
                        <p>
                          Quote link opened for{" "}
                          {values.quotationNumber || "QUO-1077"}. Viewer
                          identity unavailable; this does not record approval.
                        </p>
                      </article>
                    )}
                    {isDeclined && (
                      <article className="quotation-history-event">
                        <strong>Quote declined</strong>
                        <time>{formattedDeclinedDate}</time>
                        <span>
                          {values.declinedByName ||
                            values.contactName ||
                            "Customer"}
                        </span>
                        <p>
                          {values.declineReason ||
                            "The customer declined this quote."}
                        </p>
                      </article>
                    )}
                    {isExpired && (
                      <article className="quotation-history-event">
                        <strong>Quote expired</strong>
                        <time>{formattedExpiredDate}</time>
                        <span>System</span>
                        <p>
                          This quote reached the end of its validity period.
                        </p>
                      </article>
                    )}
                    <article className="quotation-history-event">
                      <strong>Sent quote</strong>
                      <time>{formattedSentDate}</time>
                      <span>{values.sentByName || userName}</span>
                      <p>
                        Sent {values.quotationNumber || "QUO-1077"} to{" "}
                        {values.recipientEmail ||
                          "accounts@beaconplumbing.example"}
                        .
                      </p>
                    </article>
                    <article className="quotation-history-event">
                      <strong>Created quote</strong>
                      <time>{formattedCreationDate}</time>
                      <span>{values.createdByName || userName}</span>
                      <p>
                        Created {values.quotationNumber || "QUO-1077"} for{" "}
                        {values.contactID ? (
                          <Link
                            to={`/contacts/${values.contactID}`}
                            className="quote-customer-link"
                          >
                            {values.contactName || "Beacon Plumbing"}
                          </Link>
                        ) : (
                          values.contactName || "Beacon Plumbing"
                        )}
                        .
                      </p>
                    </article>
                  </>
                )}
              </div>

              {/* Right col: Internal note composer */}
              <div className="quote-history-composer-col">
                <div className="quotation-history-composer">
                  <label htmlFor="quote-details-internal-note">
                    Add an internal note
                  </label>
                  <textarea
                    id="quote-details-internal-note"
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    placeholder="Write a note for your team…"
                  />
                  <span>Only visible to your team</span>
                  <button
                    type="button"
                    disabled={!internalNote.trim() || addingNote}
                    onClick={handleAddInternalNote}
                  >
                    <LuPlus />
                    <span>{addingNote ? "Adding…" : "Add note"}</span>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* CREATE INVOICE DIALOG — approved quote conversion */}
      {invoiceDialogOpen && (
        <div
          className="quotation-dialog-scrim quote-create-invoice-scrim"
          data-clickable={!creatingInvoice}
          onClick={(event) => {
            if (event.target === event.currentTarget && !creatingInvoice) {
              setInvoiceDialogOpen(false);
            }
          }}
        >
          <section
            className="quote-create-invoice-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-invoice-dialog-title"
          >
            <header className="quote-create-invoice-header">
              <div>
                <h2 id="create-invoice-dialog-title">Create invoice</h2>
                <p>
                  Review the details before creating an invoice from this
                  approved quote.
                </p>
              </div>
              <button
                type="button"
                className="quote-create-invoice-close"
                aria-label="Close create invoice dialog"
                disabled={creatingInvoice}
                onClick={() => setInvoiceDialogOpen(false)}
              >
                <LuX />
              </button>
            </header>

            <div className="quote-create-invoice-body">
              <div className="quote-create-invoice-source">
                <span className="quote-create-invoice-source-label">
                  Creating from quote
                </span>
                <strong>{values.quotationNumber || "QUO-1077"}</strong>
                {values.contactID ? (
                  <Link
                    to={`/contacts/${values.contactID}`}
                    className="quote-customer-link"
                  >
                    {values.contactName || "Customer"}
                  </Link>
                ) : (
                  <span>{values.contactName || "Customer"}</span>
                )}
              </div>

              <div className="quote-create-invoice-summary">
                <div>
                  <span>Invoice total</span>
                  <strong>${formatCurrencyAUD(totalAmount || 0)}</strong>
                </div>
                <div>
                  <span>Customer</span>
                  <strong>
                    {values.contactID ? (
                      <Link
                        to={`/contacts/${values.contactID}`}
                        className="quote-customer-link"
                      >
                        {values.contactName || "Customer"}
                      </Link>
                    ) : (
                      values.contactName || "Customer"
                    )}
                  </strong>
                </div>
                <div>
                  <span>Items</span>
                  <strong>{itemsList.length || 0}</strong>
                </div>
              </div>

              <div className="quote-create-invoice-note">
                <LuReceiptText />
                <p>
                  The invoice will copy the quote items, pricing and customer
                  details. You can review and edit it before sending.
                </p>
              </div>
            </div>

            <footer className="quote-create-invoice-footer">
              <button
                type="button"
                className="quote-create-invoice-cancel"
                disabled={creatingInvoice}
                onClick={() => setInvoiceDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="quote-create-invoice-confirm"
                disabled={creatingInvoice}
                onClick={handleConfirmCreateInvoice}
              >
                <LuReceiptText />
                <span>
                  {creatingInvoice ? "Creating invoice…" : "Create invoice"}
                </span>
              </button>
            </footer>
          </section>
        </div>
      )}

      {revisionDialogOpen && (
        <div
          className="quotation-dialog-scrim"
          data-clickable="true"
          onClick={() => setRevisionDialogOpen(false)}
        >
          <div
            className="quotation-modal-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="quotation-revision-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="quotation-modal-header">
              <h3 id="quotation-revision-title">Create a new revision?</h3>
              <button
                type="button"
                className="quotation-dialog-close-btn"
                aria-label="Close"
                onClick={() => setRevisionDialogOpen(false)}
              >
                <LuX />
              </button>
            </div>
            <div className="quotation-modal-body">
              <p>
                The customer’s current copy will remain unchanged. Your edits
                will be saved as a new draft revision.
              </p>
            </div>
            <div className="quotation-modal-footer">
              <button
                type="button"
                className="quotation-modal-cancel-btn"
                onClick={() => setRevisionDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="quotation-modal-submit-btn"
                onClick={() => navigate(`/quotations/edit/${QuotationID}`)}
              >
                <LuRotateCw />
                Create revision
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BILLING MODAL: If user runs out of invoice credits */}
      <BillingModal
        open={showBillingModal}
        handleConfirm={() => navigate("/subscriptions")}
        handleCancel={() => setShowBillingModal(false)}
        closeModalClick={() => setShowBillingModal(false)}
        message={genericMessage.billingMsg}
        title={genericMessage.noCreditAvailableTitle}
      />

      {/* EMAIL MODAL: Follow up / Resend */}
      {isEmailModalOpen && (
        <QuotationSendDialog
          id="followup-send-recipient"
          values={values}
          organisation={organisation}
          currencyCode={currencyCode}
          recipient={sendRecipient}
          recipientError={recipientError}
          additionalRecipientsError={additionalRecipientsError}
          onRecipientsChange={(emails) => {
            setAdditionalRecipients(emails);
            setSendError("");
          }}
          onRecipientsError={setAdditionalRecipientsError}
          subject={sendSubject}
          onSubjectChange={setSendSubject}
          editableMessage={emailAction === "followUp"}
          message={sendMessage}
          onMessageChange={(message) => {
            setSendMessage(message);
            setSendError("");
          }}
          attachPdf={attachPdf}
          onAttachPdfChange={setAttachPdf}
          sendCopy={sendCopy}
          onSendCopyChange={setSendCopy}
          busy={sendingEmail}
          sendingEmail={sendingEmail}
          error={sendError}
          onClose={() => {
            if (!sendInFlightRef.current) setIsEmailModalOpen(false);
          }}
          onSend={handleSendEmail}
        />
      )}
      <DocumentSentSuccessDialog
        open={showSentSuccess}
        documentType="quote"
        documentNumber={values.quotationNumber}
        recipient={sendRecipient}
        onClose={() => setShowSentSuccess(false)}
        onViewDetails={() => {
          setShowSentSuccess(false);
          navigate(`/quotations/view/${QuotationID}`);
        }}
      />

      {/* DOCUMENT VIEWER MODAL */}
      {documentViewerOpen && (
        <div
          className="quotation-dialog-scrim document-preview-overlay"
          data-clickable="true"
          onClick={() => {
            setDocumentViewerOpen(false);
            setActiveDocumentUrl("");
          }}
        >
          <div
            className="quotation-document-preview"
            role="dialog"
            aria-modal="true"
            aria-label={activeDocumentTitle || "Attachment preview"}
            tabIndex={-1}
            data-clickable={false}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="quotation-document-preview-close"
              aria-label="Close document preview"
              onClick={() => {
                setDocumentViewerOpen(false);
                setActiveDocumentUrl("");
              }}
            >
              <LuX />
            </button>
            {activeDocumentType === "image" ? (
              <img src={activeDocumentUrl} alt={activeDocumentTitle} />
            ) : activeDocumentType === "pdf" ? (
              <iframe
                title={activeDocumentTitle || "Quotation attachment"}
                className="quotation-document-preview-pdf"
                src={`${activeDocumentUrl}#toolbar=0&navpanes=0&view=FitH`}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {activeDocumentType === "text" ? (
                  <pre
                    style={{
                      margin: 0,
                      padding: "20px",
                      flex: 1,
                      minHeight: 0,
                      overflow: "auto",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      fontFamily: "var(--font-family-primary)",
                      fontSize: "14px",
                      color: "var(--text-primary)",
                    }}
                  >
                    {activeDocumentText}
                  </pre>
                ) : (
                  <p style={{ padding: "20px", margin: 0 }}>
                    Preview is not available for this file type.
                  </p>
                )}
                <a
                  className="quotation-sidebar-download-btn"
                  href={activeDocumentUrl}
                  download={activeDocumentName}
                >
                  Download attachment
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewQuotation;
